"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

// Public embed token for the GoHighLevel / LeadConnector chat widget. Not a
// secret — it's served to every visitor in the widget script tag — so it's
// hardcoded rather than env-gated, keeping the widget live on every deploy
// (a forgotten Vercel var would silently fail the GHL compliance check).
const LEADCONNECTOR_WIDGET_ID = "6a651a45c4dda61f3fb77a22";
const LOADER_SRC = "https://widgets.leadconnectorhq.com/loader.js";
const RESOURCES_URL =
  "https://widgets.leadconnectorhq.com/chat-widget/loader.js";

// URL prefixes of the authenticated product — these mirror the
// app/(dashboard)/ route group. The chat widget loads everywhere EXCEPT here,
// so keep this list in sync if a new dashboard section is added.
const APP_ROUTE_PREFIXES = [
  "/dashboard",
  "/lists",
  "/my-searches",
  "/settings",
  "/admin",
];

export function isAuthenticatedAppPath(pathname: string): boolean {
  return APP_ROUTE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

// Inert compliance marker: the full widget embed as a raw HTML string, but
// (a) type="text/plain" so the browser never executes it during the initial
// parse, and (b) delivered via dangerouslySetInnerHTML so React treats it as
// opaque markup and never turns it into a fiber. Both halves matter:
//
//   • It ships inside the server-rendered HTML, so GoHighLevel's compliance
//     crawler — which does not run JS — detects the widget by its
//     data-widget-id / loader.js src.
//   • Because React never reconciles the inner <script>, React 19's script
//     hoisting leaves it alone. A raw <script src> React ELEMENT gets hoisted
//     and reordered next to the PostHog + GA <script>s (see app/layout.tsx);
//     its server/client order then disagrees with theirs, which is the
//     homepage React #418 hydration mismatch (client's leadconnector <script>
//     vs server's posthog array.js <script>). Opaque HTML sidesteps hoisting.
const COMPLIANCE_MARKER = `<script type="text/plain" src="${LOADER_SRC}" data-resources-url="${RESOURCES_URL}" data-widget-id="${LEADCONNECTOR_WIDGET_ID}" data-source="WEB_USER"></script>`;

// Load-once guard: the loader mounts a persistent widget that survives client
// navigations, so a re-mount of this component must not append a second loader.
let loaderInjected = false;

export default function LeadConnectorWidget() {
  const pathname = usePathname();
  const active = !isAuthenticatedAppPath(pathname);

  useEffect(() => {
    if (!active || loaderInjected) return;
    // Load the REAL (executable) loader AFTER hydration, appended straight to
    // <body> — outside React's tree. The loader injects a <chat-widget> node
    // with a Date.now()-based id; doing that mid-hydration (as the native async
    // <script> in the initial HTML did) raced the tree React was hydrating —
    // the #418. A post-hydration append into the DOM can't race React.
    loaderInjected = true;
    const script = document.createElement("script");
    script.src = LOADER_SRC;
    script.async = true;
    script.dataset.resourcesUrl = RESOURCES_URL;
    script.dataset.widgetId = LEADCONNECTOR_WIDGET_ID;
    script.dataset.source = "WEB_USER";
    document.body.appendChild(script);
  }, [active]);

  if (!active) return null;

  return <div dangerouslySetInnerHTML={{ __html: COMPLIANCE_MARKER }} />;
}
